/**
 * Per-shape eye styling for customiser silhouettes.
 *
 * Resting states (`baseFace`) reshape the chosen expression on concave profiles.
 * Catalogue faces (`wink`, `wide`, `notify`) keep their video geometry on the
 * circle; on a pinched waist they scale to fit the pocket — otherwise `wide`'s
 * 87-unit capsules fill the star like two black columns.
 *
 * On the default star the trap is the « ghost »: flattening every expression into
 * horizontal slits reads as a sheet with two holes, not a face. Squints and arcs
 * need taller capsules and gentler tilts; neutral eyes stay vertical pills.
 *
 * Applied in the engine at render time and mirrored in `eyefit.ts` when building
 * the offset table — never as a post-pass in the Vue component.
 */

import type { HeadGaze } from './face'
import { REST_GAZE, gazeAxes } from './face'
import { clamp, lerp } from './math'
import { radiusAtAngle } from './shape'
import type { EyeCfg, StateDef } from './states'
import type { BotExpression, ExpressionId } from './expressions'
import { SHAPE_BY_ID } from './skins'

/** Concave-profile gaze nudge — import-time constant, not a per-frame solver. */
export interface ShapeGazeBias {
  yaw: number
  pitch: number
}

export interface ShapeEyeStyle {
  w: number
  h: number
  spread: number
  lift: number
  /** Pull sphere position toward the centre before radius snap — concave shapes only. */
  inset: number
  /** Multiplier on the expression's per-eye tilt — damped on pinched profiles. */
  tilt: number
}

export const UNIT_EYE: ShapeEyeStyle = { w: 1, h: 1, spread: 1, lift: 0, inset: 1, tilt: 1 }

/**
 * Catalogue moods from Attentive through Scared. On the sparkle, orbit seats use
 * neutre half-spread and tip `pairScale` collapses the pair — these ids share the
 * Surprised gap lock (min/max sep, no tip pairScale).
 */
export const STAR_FOLLOW_GAP_IDS: readonly ExpressionId[] = [
  'attentif',
  'surpris',
  'excite',
  'heureux',
  'hilare',
  'colere',
  'triste',
  'effraye'
]

export function starFollowGapMood(id: string | undefined | null): boolean {
  return !!id && (STAR_FOLLOW_GAP_IDS as readonly string[]).includes(id)
}

/** Min radius / max radius — low means pinched waist (star), high means round (circle). */
export function profileWaist(radii: number[]): number {
  const peak = Math.max(...radii)
  if (peak <= 0) return 1
  return Math.min(...radii) / peak
}

/**
 * How much the lower arc pinches vs the flanks — star waist, capsule dome, smile cheek.
 * θ = 0 is right, increasing clockwise (y down).
 */
export function bottomArcPinch(radii: number[]): number {
  const n = radii.length
  const peak = Math.max(...radii)
  const flank = (radii[0]! + radii[Math.round(n / 2)]!) / 2
  let pinch = 0
  const i0 = Math.round(n / 8)
  const i1 = Math.round((3 * n) / 8)
  for (let i = i0; i <= i1; i++) pinch = Math.max(pinch, (flank - radii[i]!) / peak)
  return clamp(pinch, 0, 0.5)
}

/** Seat eyes above a curved lower arc — stronger pinch ⇒ higher on screen. */
export function bottomCurveLift(radii: number[] | null): number {
  if (!radii || radii === ETOILE_RADII) return 0
  const pinch = bottomArcPinch(radii)
  if (pinch < 0.02) return 0
  const t = concaveBlendT(radii)
  return lerp(0, -0.028, clamp(pinch / 0.14, 0, 1)) * Math.max(t, 0.3)
}

/** Horizontal slit with little tilt — `blase`, nearly closed lids. */
function isSlit(w: number, h: number, tilt: number): boolean {
  return h / w < 0.55 && Math.abs(tilt) < 10
}

/** Narrowed lids without an arched tilt — `mefiant` half-close, `timide` peek. */
function isNarrow(w: number, h: number, tilt: number): boolean {
  const aspect = h / w
  return aspect < 0.88 && Math.abs(tilt) < 8
}

/** Laugh crescents — partly closed, strong ^ tilt, bottom edge curves inward. */
function isLaughCrescent(w: number, h: number, tilt: number, open = 1): boolean {
  return open < 0.9 && tilt < -14 && h / w < 0.38
}

/** Closed laugh arcs — flat capsules, strong negative tilt (^ ^, outer corners up). */
function isLaughArc(w: number, h: number, tilt: number): boolean {
  return tilt < -18 && h / w < 0.34
}

/** Open smile eyes — tall pills, mild negative tilt (outer corners up). */
function isSmileEye(w: number, h: number, tilt: number): boolean {
  return tilt < -5 && tilt > -14 && h / w >= 1.25 && h / w < 1.75
}

/** Angry ∧ slashes — flat capsules, strong positive tilt. */
function isAngryArc(w: number, h: number, tilt: number): boolean {
  return tilt > 14 && h / w < 0.4
}

/** Sad ∨ droop — tall narrow eyes, strong negative tilt (never open smile pills). */
function isSadArc(w: number, h: number, tilt: number): boolean {
  return tilt < -12 && h / w >= 1.75
}

/** Arched squint — flat capsules where tilt carries the read (excludes sad/angry). */
function isSquint(w: number, h: number, tilt: number): boolean {
  if (isLaughArc(w, h, tilt) || isAngryArc(w, h, tilt)) return true
  return Math.abs(tilt) >= 12 && h / w < 0.55
}

/** Wide round eyes — `surpris`, `effraye`. */
function isRound(w: number, h: number): boolean {
  const aspect = h / w
  return w >= 0.34 && aspect >= 0.85 && aspect <= 1.35
}

/** Horizontal dash eye — `mefiant` slit, `blase` bored lid. */
function isDash(w: number, h: number): boolean {
  return h / w < 0.5 && h < 0.14
}

/** Inward-leaning tall squint — `confus` cross-eye (not worried `attentif`). */
function isConvergingSquint(w: number, h: number, tilt: number): boolean {
  return tilt > 12 && h / w >= 1.2 && h / w < 2
}

/** Small peeking pills — reserved; `timide` seats via pitch + pocket lift. */
function isPeeking(_w: number, _h: number): boolean {
  return false
}

/**
 * Target eye multipliers for a concave customiser shape. Expression-aware.
 *
 * Never flatten neutral eyes into horizontal slits. Squints get *more* height on
 * the star so arcs stay legible; tilts are scaled down so diagonals don't become
 * random scratches on a pinched waist.
 */
export function concaveEyeMul(
  w: number,
  h: number,
  tilt = 0,
  open = 1,
  form: EyeCfg['form'] = 'capsule'
): ShapeEyeStyle {
  const inset = 0.9
  const spread = 0.97
  if (form === 'smile') {
    const pinched = open < 0.9
    return {
      // Open Excited discs were ~r22 at sep 42 (still fused). Shrink open smiles;
      // Laughing (pinched) stays thick.
      w: pinched ? 1.02 : 0.88,
      h: pinched ? 1.1 : 0.92,
      spread: 1.22,
      lift: pinched ? -0.004 : -0.012,
      inset: 0.91,
      tilt: 0.96
    }
  }
  if (open < 0.9) {
    if (isLaughCrescent(w, h, tilt, open)) {
      return {
        w: 0.92,
        h: 1.55,
        spread,
        lift: -0.006,
        inset: 0.91,
        tilt: Math.abs(tilt) >= 22 ? 0.66 : 0.72
      }
    }
    return { w: 1.02, h: 0.96, spread, lift: -0.028, inset, tilt: 0.82 }
  }
  if (isSlit(w, h, tilt)) {
    return { w: 0.9, h: 1.28, spread, lift: -0.02, inset, tilt: 0.35 }
  }
  if (isDash(w, h)) {
    return { w: 1.02, h: 1.22, spread, lift: -0.026, inset: 0.91, tilt: 0.42 }
  }
  if (isPeeking(w, h)) {
    return { w: 0.88, h: 1.45, spread, lift: -0.024, inset: 0.91, tilt: 0.78 }
  }
  if (isConvergingSquint(w, h, tilt)) {
    return { w: 0.82, h: 1.35, spread, lift: -0.022, inset: 0.91, tilt: 0.62 }
  }
  if (isNarrow(w, h, tilt)) {
    return { w: 0.76, h: 1.68, spread, lift: -0.024, inset, tilt: 0.84 }
  }
  // Smile — open ^ eyes (cheeks lift outer corners). Not a suspicious inward squint.
  if (isSmileEye(w, h, tilt)) {
    return { w: 1.03, h: 1.02, spread, lift: -0.016, inset: 0.91, tilt: 0.9 }
  }
  // Sad — tall open ∨ droop (outer corners down). NOT a flat angry slash.
  if (isSadArc(w, h, tilt)) {
    return {
      w: 1.02,
      h: 1.08,
      spread: 1.18,
      lift: -0.032,
      inset: 0.91,
      tilt: Math.abs(tilt) >= 16 ? 0.82 : 0.88
    }
  }
  if (isSquint(w, h, tilt)) {
    const flat = h / w < 0.45
    // Laughing — closed ^^ arcs (thick, outer corners up). Not an angry ∧ slash.
    if (isLaughArc(w, h, tilt)) {
      return {
        w: 0.94,
        h: 1.52,
        spread,
        lift: -0.012,
        inset: 0.91,
        tilt: Math.abs(tilt) >= 22 ? 0.68 : 0.74
      }
    }
    // Angry ∧ slashes.
    if (isAngryArc(w, h, tilt)) {
      return {
        w: 0.84,
        h: 1.38,
        spread: 1.18,
        lift: -0.02,
        inset: 0.9,
        tilt: Math.abs(tilt) >= 32 ? 0.5 : 0.58
      }
    }
    const tScale = Math.abs(tilt) >= 20 ? 0.44 : 0.56
    return {
      w: flat ? 0.86 : 0.94,
      h: flat ? 1.42 : 1.18,
      spread,
      lift: -0.024,
      inset,
      tilt: tScale
    }
  }
  if (isRound(w, h)) {
    // Surprised + Scared discs: full size on the star fuses into one mask hole.
    return {
      w: 0.78,
      h: 0.78,
      spread: 1.22,
      lift: -0.018,
      inset: 0.86,
      tilt: 0.86
    }
  }
  // Neutral tall capsules — seated toward the top-tip face orbit.
  // Attentive (taller/wider than neutre) gets extra seat spread; neutre stays put.
  const attentifLike = w >= 0.22 && w < 0.34 && h / w >= 1.35
  return {
    w: 1.0,
    h: 0.9,
    spread: attentifLike ? 1.16 : spread,
    lift: -0.05,
    inset,
    tilt: 0.88
  }
}

/** Blend strength for concave styling — circle ≈ 0, sparkle star ≈ 1. */
export function concaveBlendT(radii: number[] | null): number {
  if (!radii) return 0
  const waist = profileWaist(radii)
  return clamp((0.8 - waist) / (0.8 - 0.61), 0, 1)
}

/**
 * Slight look-down on pinched waists: pitches the sphere so eyes read as carved
 * into the body, not painted on a flat sheet. Tabulated per shape, blended at
 * morph boundaries like `eyeStyleForCfg`.
 */
export function shapeGazeBias(radii: number[] | null): ShapeGazeBias {
  if (radii === JOVI_RADII) {
    // No pitch bias — mid-dome seating is `roundBodyEyeSeat` lift (pitch-scaled
    // in the engine). A fixed look-down stacked on every expression gaze and
    // read as permanently staring into the ring.
    return { yaw: 0, pitch: 0 }
  }
  const t = concaveBlendT(radii)
  if (t <= 0) return { yaw: 0, pitch: 0 }
  // Mild look-down only — stronger −3° sat eyes in the waist; the face orbit
  // (sphere under the top tip) reads more realistic on the sparkle.
  return { yaw: 0, pitch: lerp(0, -1.2, t) }
}

/** Default star profile — expression pocket tables measured on this shape. */
const ETOILE_RADII = SHAPE_BY_ID.get('etoile')!.radii
/** Jovi (Mickey + ring) — face seats mid-dome, not on the circle crown. */
const JOVI_RADII = SHAPE_BY_ID.get('jovi')!.radii
/** Peta (five soft lobes) — face seats in the petal pocket, not the top tip. */
const PETA_RADII = SHAPE_BY_ID.get('peta')!.radii

/**
 * Round-body face seat. Circle REST puts eyes at meanY ≈ −46; on Jovi that
 * reads as a forehead because the ground lobes drop the visual mass. Drop the
 * pair toward the Saturn belt (mid-dome). Keep inset at 1 — pulling them in
 * made rim looks feel sunk into the dome instead of sitting on it.
 *
 * The lift is the *REST* correction only — `volumeBodyLiftScale(pitch)` fades it
 * when an expression already looks down, so moods don't sink into the ring.
 */
export function roundBodyEyeSeat(radii: number[] | null): ShapeEyeStyle | null {
  if (!radii || radii !== JOVI_RADII) return null
  return {
    w: 1,
    h: 1,
    spread: 1.04,
    lift: 0.16,
    inset: 1,
    tilt: 1
  }
}

/**
 * Peta face seat. Circle REST sits high in the top petal; drop the pair into
 * the chubby face pocket. Keep inset under 1 so rim looks don't clip the
 * petal edge (Jovi wants inset 1 on the smooth dome — soft lobes don't).
 */
export function petaBodyEyeSeat(radii: number[] | null): ShapeEyeStyle | null {
  if (!radii || radii !== PETA_RADII) return null
  return {
    w: 1,
    h: 1,
    spread: 1.0,
    lift: 0.06,
    inset: 0.96,
    tilt: 1
  }
}

/**
 * Peta's face-on rest — centred under the top petal. Video `REST_GAZE` yaw
 * parks the pair in the right lobe on the five-fold glyph.
 */
export const PETA_REST_GAZE: HeadGaze = { yaw: 0, pitch: 22, roll: 0 }

/**
 * Remap a head gaze so Peta's REST lands on `PETA_REST_GAZE`.
 *
 * Video REST (yaw≈28) is "looking straight" on the circle; catalogue moods use
 * yaw≈0–16 for the same idea. Remapping both as `yaw − REST` dumps surpris /
 * attentif into the left petal (−20°). Parked faces compress that
 * more-camera-than-REST gap into a glance; live pointer / body drive keeps the
 * full throw so free-look still crosses the face.
 */
export function petaSeatGaze(
  gaze: HeadGaze,
  opts: { live?: boolean } = {}
): HeadGaze {
  const dy = gaze.yaw - REST_GAZE.yaw
  const dp = gaze.pitch - REST_GAZE.pitch
  const live = opts.live === true
  // Negative dy = more camera-facing than video REST. On Peta that REST is
  // already face-on, so parked moods only keep a glance — not a petal dive.
  const yawK = live ? 0.78 : dy < 0 ? 0.1 : 0.45
  const pitchK = live ? 0.7 : 0.1
  return {
    yaw: clamp(PETA_REST_GAZE.yaw + dy * yawK, -48, 48),
    // Parked floor stays near the face pocket — surpris pitch −3 used to climb
    // into the top petal tip (midY≈−45) when the floor was 6°.
    pitch: clamp(
      PETA_REST_GAZE.pitch + dp * pitchK,
      live ? 6 : 18,
      live ? 44 : 32
    ),
    roll: gaze.roll
  }
}

/** Jovi or Peta — soft volume faces that share slant / lean / lift scaling. */
export function volumeBodyEyeSeat(radii: number[] | null): ShapeEyeStyle | null {
  return roundBodyEyeSeat(radii) ?? petaBodyEyeSeat(radii)
}

/**
 * How much of the mid-face lift to keep at this gaze pitch. Full at REST;
 * eases off as the face already looks down (surpris, blase, somnolent…).
 */
export function volumeBodyLiftScale(pitch: number): number {
  const down = clamp((REST_GAZE.pitch - pitch) / 36, 0, 1)
  return 1 - down * 0.9
}

/** @deprecated Use `volumeBodyLiftScale`. */
export const roundBodyLiftScale = volumeBodyLiftScale

/** Concave customiser shapes that carry a waist pocket. */
function starPocketT(radii: number[] | null): number {
  if (!radii || radii !== ETOILE_RADII) return 0
  return concaveBlendT(radii)
}

/** Catalogue faces on any concave customiser body. */
function cataloguePocketT(radii: number[] | null): number {
  return concaveBlendT(radii)
}

/**
 * How strongly to pull gaze pitch toward the neutral pocket. Asymmetric and
 * high-roll moods keep more of their measured tilt so they do not shear apart.
 */
export function expressionSeatStrength(expr: BotExpression): number {
  const [a, b] = expr.eyes
  const sizeAsym = Math.abs(a.w - b.w) + Math.abs(a.h - b.h)
  const roll = Math.abs(expr.gaze.roll)
  const mirroredTilt = Math.abs((a.tilt ?? 0) + (b.tilt ?? 0)) < 2
  const converging =
    !mirroredTilt && Math.abs(a.tilt ?? 0) > 12 && Math.abs(b.tilt ?? 0) > 12
  if (sizeAsym > 0.12) return 0.52
  if (roll > 11) return 0.58
  if (converging) return 0.55
  return 1
}

function pocketPitchDelta(pitch: number, strength: number, t: number): number {
  if (t <= 0) return 0
  return lerp(0, (REST_GAZE.pitch - pitch) * strength, t)
}

const POCKET_LIFT: Partial<Record<ExpressionId, number>> = {
  attentif: 0.148,
  surpris: 0.17,
  excite: 0.167,
  heureux: 0.157,
  hilare: 0.17,
  colere: 0.108,
  triste: 0.121,
  effraye: 0.182,
  mefiant: 0.098,
  confus: 0.245,
  curieux: 0.182,
  fier: 0.055,
  timide: -0.074,
  blase: -0.091,
  somnolent: 0.053
}

/** Fine vertical seat — standard moods only; asymmetric moods rely on pitch. */
export function expressionPocketLift(
  expr: BotExpression | null,
  radii: number[] | null
): number {
  if (!expr || expr.id === 'neutre') return 0
  const t = starPocketT(radii)
  if (t <= 0 || expressionSeatStrength(expr) < 0.5) return 0
  const lift = POCKET_LIFT[expr.id] ?? 0
  if (lift === 0) return 0
  return lerp(0, -lift, t)
}

function animationSeatStrength(stateId: string): number {
  if (stateId.startsWith('agent')) return 0
  if (stateId === 'wide') return 0.38
  if (stateId === 'notify') return 0.12
  if (stateId === 'wink' || stateId === 'blink') return 0.72
  return 0.78
}

/** Catalogue animation faces on a concave body — toward the idle waist pocket. */
export function animationPocketPitch(
  gaze: HeadGaze,
  radii: number[] | null,
  stateId = ''
): number {
  return animationPocketBias(gaze, radii, stateId).pitch
}

/** Yaw + pitch onto the idle pocket axis. Roll is eased in the engine. */
export function animationPocketBias(
  gaze: HeadGaze,
  radii: number[] | null,
  stateId = ''
): ShapeGazeBias {
  const t = cataloguePocketT(radii)
  const strength = animationSeatStrength(stateId)
  if (t <= 0 || strength <= 0) return { yaw: 0, pitch: 0 }
  return {
    yaw: lerp(0, (REST_GAZE.yaw - gaze.yaw) * strength, t),
    pitch: lerp(0, (REST_GAZE.pitch - gaze.pitch) * strength, t)
  }
}

/** How much to ease measured roll toward idle's `\\` on a concave waist. */
export function animationPocketRoll(
  roll: number,
  radii: number[] | null,
  stateId = ''
): number {
  const t = cataloguePocketT(radii)
  const strength = animationSeatStrength(stateId)
  if (t <= 0 || strength <= 0) return roll
  if (stateId === 'wink') return roll
  return lerp(roll, REST_GAZE.roll, t * strength)
}

const ANIMATION_POCKET_LIFT: Record<string, number> = {
  wink: 0.042,
  wide: 0.04,
  notify: 0.028,
  blink: 0.036,
  connect: 0.034,
  agentThink: 0.038,
  agentRun: 0.034,
  agentTool: 0.036,
  agentConnect: 0.035,
  agentSearch: 0.033,
  agentStream: 0.031,
  agentWait: 0.04,
  agentOk: 0.032,
  agentFail: 0.032,
  agentCancel: 0.03,
  agentHandoff: 0.034,
  agentPair: 0.032,
  agentReview: 0.035,
  agentSync: 0.033,
  agentChat: 0.031
}

/** @deprecated Lift is folded into `concavePocketStyle`. */
export function animationPocketLift(
  stateId: string,
  _gaze: HeadGaze,
  radii: number[] | null
): number {
  void stateId
  void _gaze
  void radii
  return 0
}

/** `baseBody` states that keep a measured face (catalogue, loaders, agents). */
export function isConcavePocketState(def: Pick<StateDef, 'baseBody' | 'baseFace'>): boolean {
  return def.baseBody && !def.baseFace
}

/**
 * On concave customiser shapes, pitch each expression toward neutral's waist pocket.
 * Measured gaze pitch is circle-relative; without this, moods sit 20–40 px too low on the star.
 */
export function expressionPocketPitch(
  expr: BotExpression | null,
  radii: number[] | null
): number {
  if (!expr || expr.id === 'neutre') return 0
  return pocketPitchDelta(expr.gaze.pitch, expressionSeatStrength(expr), starPocketT(radii))
}

/**
 * Gaze bias for any face on a concave customiser shape — expressions, catalogue
 * animations (`wide`, `orbit`…), and agent loops.
 */
export function pocketGazeBias(
  radii: number[] | null,
  gaze: HeadGaze,
  expr: BotExpression | null,
  catalogue = false,
  stateId = ''
): ShapeGazeBias {
  const base = shapeGazeBias(radii)
  if (expr) {
    return { yaw: base.yaw, pitch: base.pitch + expressionPocketPitch(expr, radii) }
  }
  if (catalogue) {
    const extra = animationPocketBias(gaze, radii, stateId)
    return { yaw: base.yaw + extra.yaw, pitch: base.pitch + extra.pitch }
  }
  return base
}

/** @deprecated Use `pocketGazeBias`. */
export function restingGazeBias(
  radii: number[] | null,
  expr: BotExpression | null
): ShapeGazeBias {
  return pocketGazeBias(radii, expr?.gaze ?? REST_GAZE, expr, false)
}

/** Interpolate gaze bias across a shape morph (same boundaries as eye style). */
export function lerpGazeBias(a: ShapeGazeBias, b: ShapeGazeBias, t: number): ShapeGazeBias {
  return { yaw: lerp(a.yaw, b.yaw, t), pitch: lerp(a.pitch, b.pitch, t) }
}

/** Apply a bias to a head gaze — used by the engine and the eyefit table builder. */
export function gazeWithBias(gaze: HeadGaze, bias: ShapeGazeBias): HeadGaze {
  return { yaw: gaze.yaw + bias.yaw, pitch: gaze.pitch + bias.pitch, roll: gaze.roll }
}

/** Scale video capsules so they fit a pinched waist without becoming idle. */
const CATALOGUE_POCKET_SIZE: Record<string, { w: number; h: number; inset: number; spread: number }> = {
  // Tall 0.356×0.875 pills fill the star; round them toward open surpris.
  wide: { w: 0.82, h: 0.58, inset: 0.86, spread: 0.94 },
  // Round notify discs: keep them large, just short of flooding the pocket.
  notify: { w: 0.86, h: 0.88, inset: 0.9, spread: 0.94 }
}

/** Seating on concave shapes without reshaping measured video capsules. */
export function concavePocketStyle(radii: number[] | null, stateId = ''): ShapeEyeStyle | null {
  const t = concaveBlendT(radii)
  if (t <= 0) return null
  const extra = ANIMATION_POCKET_LIFT[stateId] ?? 0.04
  const size = CATALOGUE_POCKET_SIZE[stateId] ?? { w: 1, h: 1, inset: 0.9, spread: 0.965 }
  return {
    w: lerp(1, size.w, t),
    h: lerp(1, size.h, t),
    spread: lerp(1, size.spread, t),
    lift: lerp(0, -0.032 - extra, t),
    inset: lerp(1, size.inset, t),
    tilt: 1
  }
}

/** @deprecated Use `concavePocketStyle`. */
export function agentEyeStyle(radii: number[] | null): ShapeEyeStyle | null {
  return concavePocketStyle(radii)
}

/** Light waist damp on agent star — pose carries the sweep, not the engine. */
export function agentConcaveGaze(radii: number[] | null, gaze: HeadGaze): HeadGaze {
  const t = concaveBlendT(radii)
  if (t <= 0) return gaze
  const keep = 1 - t * 0.2
  return { yaw: gaze.yaw * keep, pitch: gaze.pitch * keep, roll: gaze.roll }
}

/**
 * Soft-limit look on a concave body. Absolute scale would drag REST off its
 * pocket; this only compresses angles past a star-safe max, and only as `mix`
 * rises (pointer / free look).
 */
export function softConcaveGaze(
  radii: number[] | null,
  gaze: HeadGaze,
  mix: number
): HeadGaze {
  const t = concaveBlendT(radii)
  const m = clamp(mix, 0, 1)
  if (t <= 0 || m < 0.05) return gaze
  // Keep room past REST_GAZE (~28°/29°) so look-right still travels; tip-cap
  // / nest catch clipping. Was 30/22 — crushed free-right to a spin in place.
  const maxYaw = lerp(52, 40, t)
  const maxPitch = lerp(44, 30, t)
  const soft = lerp(1, 0.5, m * t)
  const compress = (v: number, max: number) => {
    const a = Math.abs(v)
    if (a <= max) return v
    return Math.sign(v) * (max + (a - max) * soft)
  }
  return {
    yaw: compress(gaze.yaw, maxYaw),
    pitch: compress(gaze.pitch, maxPitch),
    roll: gaze.roll
  }
}

/**
 * Common radial scale that keeps a pair inside the face pocket of a concave
 * outline. Uniform from the origin — preserves left-higher rail geometry.
 * Without this, sphere look rides a tip edge and eyes read as stuck on anyhow.
 *
 * `onlyIfOver`: if set, leave the pair alone unless some eye exceeds this
 * fraction of the local outline (clipping territory). Lets pocket spread-on-
 * look-down keep working while still catching a tip hug.
 */
export function eyePairPocketScale(
  positions: readonly { x: number; y: number }[],
  radii: number[] | null,
  scale: number,
  rot = 0,
  frac = 0.48,
  onlyIfOver = 0
): number {
  if (!radii || positions.length === 0) return 1
  const t = concaveBlendT(radii)
  if (t <= 0) return 1
  if (onlyIfOver > 0) {
    let maxRatio = 0
    for (const p of positions) {
      const r = Math.hypot(p.x, p.y)
      if (r < 1e-6) continue
      const bord = radiusAtAngle(radii, Math.atan2(p.y, p.x) - rot) * scale
      maxRatio = Math.max(maxRatio, r / Math.max(bord, 1e-6))
    }
    if (maxRatio <= onlyIfOver) return 1
  }
  const limFrac = lerp(0.78, frac, t)
  let s = 1
  for (const p of positions) {
    const r = Math.hypot(p.x, p.y)
    if (r < 1e-6) continue
    const bord = radiusAtAngle(radii, Math.atan2(p.y, p.x) - rot) * scale
    const lim = bord * limFrac
    if (r > lim) s = Math.min(s, lim / r)
  }
  return s
}

/**
 * Translate the pair so its midpoint sits in the look-side face pocket of the
 * drawn outline. Same isometry idea as eyefit — relative eye geometry (and the
 * left-higher rail) stay intact; only the pair moves as a whole.
 */
export function eyePairNestOffset(
  positions: readonly { x: number; y: number }[],
  gaze: HeadGaze,
  radii: number[] | null,
  scale: number,
  rot = 0,
  amount = 1
): { dx: number; dy: number } {
  if (!radii || positions.length === 0) return { dx: 0, dy: 0 }
  const t = concaveBlendT(radii) * clamp(amount, 0, 1)
  if (t <= 0) return { dx: 0, dy: 0 }
  const { f } = gazeAxes({ yaw: gaze.yaw, pitch: gaze.pitch, roll: 0 })
  const face = Math.hypot(f[0], f[1])
  if (face < 0.06) return { dx: 0, dy: 0 }
  const ang = Math.atan2(f[1], f[0])
  const bord = radiusAtAngle(radii, ang - rot) * scale
  // Inner face of the look-side lobe — not the tip rim.
  const nest = bord * lerp(0.48, 0.36, t)
  const tx = Math.cos(ang) * nest
  const ty = Math.sin(ang) * nest
  let mx = 0
  let my = 0
  for (const p of positions) {
    mx += p.x
    my += p.y
  }
  mx /= positions.length
  my /= positions.length
  const k = t * lerp(0.5, 0.88, clamp(face / 0.42))
  return { dx: (tx - mx) * k, dy: (ty - my) * k }
}

/**
 * Sparkle free-look: pin the pair in the crown face — the join where the left
 * and right arms meet the top tip. Yaw only slides mid-X inside that band
 * (REST ↔ horizontal flip); never nests onto a side-arm tip.
 *
 * Look-left needs a harder pull: volume foreshortening grows the left tip and
 * surface-fit rides it, so a REST-strength nest leaves the pair stuck out on
 * the arm instead of the flipped crown pocket.
 */
export function eyePairStarHeadOffset(
  positions: readonly { x: number; y: number }[],
  gaze: HeadGaze,
  radii: number[] | null,
  scale: number,
  rot = 0,
  amount = 1
): { dx: number; dy: number } {
  if (!radii || positions.length === 0) return { dx: 0, dy: 0 }
  const t = concaveBlendT(radii) * clamp(amount, 0, 1)
  if (t <= 0) return { dx: 0, dy: 0 }
  const headAng = -Math.PI / 2
  const bord = radiusAtAngle(radii, headAng - rot) * scale
  // Inside the top tip, above the L/R arm valleys (~0.6·R at ±45°).
  const headY = -bord * 0.355
  const yawK = clamp(gaze.yaw / Math.max(Math.abs(REST_GAZE.yaw), 1), -1.1, 1.1)
  // Match idle mid-X (~0.23·R) so a full left look lands as its horizontal flip.
  const headX = yawK * bord * 0.23
  // Pitch eases within the crown — down may drop further (bottom-left), never waist.
  const down = clamp((REST_GAZE.pitch - gaze.pitch) / 38, -0.55, 1)
  const ty = headY + down * bord * (down > 0 ? 0.2 : 0.1)
  let mx = 0
  let my = 0
  for (const p of positions) {
    mx += p.x
    my += p.y
  }
  mx /= positions.length
  my /= positions.length
  // Stronger when left of REST: tip growth otherwise wins over the crown pin.
  const leftPull = gaze.yaw < REST_GAZE.yaw * 0.35 ? lerp(1, 2.05, clamp(-yawK, 0, 1)) : 1
  const k = Math.min(0.96, t * 0.94 * leftPull)
  return { dx: (headX - mx) * k, dy: (ty - my) * k }
}

/**
 * Sparkle free-look seats on a flip-symmetric orbit.
 *
 * REST is the home pocket (up-right). Pointer direction moves the pair smoothly
 * around that orbit; full left is the horizontal flip of the matching right
 * depth — tip nest included — so a screenshot flipped matches the other side.
 * Sphere drafts still carry tilt/depth; this only relocates the pair.
 */
export function eyePairStarOrbitSeats(
  positions: readonly { x: number; y: number }[],
  pocket: { nx: number; ny: number },
  radii: number[] | null,
  scale: number,
  amount = 1
): { x: number; y: number }[] {
  if (!radii || positions.length < 2) {
    return positions.map((p) => ({ x: p.x, y: p.y }))
  }
  const t = concaveBlendT(radii) * clamp(amount, 0, 1)
  if (t <= 0) return positions.map((p) => ({ x: p.x, y: p.y }))

  // Idle mid / half-spread — measured off engine.sample at centred free look.
  const restMidX = scale * 0.226
  const restMidY = scale * -0.383
  const relLX = -scale * 0.146
  const relLY = scale * 0.027
  const relRX = scale * 0.146
  const relRY = -scale * 0.027

  const { nx, ny } = pocket
  const up = Math.max(0, -ny)
  const down = Math.max(0, ny)
  // Same tip depth both sides — flip(TR) == TL by construction.
  const tip = scale * (0.18 * Math.abs(nx) + 0.08 * up)

  let midX: number
  if (nx >= 0) {
    midX = restMidX + tip
  } else {
    const k = -nx
    midX = lerp(restMidX, -(restMidX + tip), k)
  }
  const midY = restMidY - up * scale * 0.12 + down * scale * 0.14

  // Unflipped pair (REST right-high `\\`); flipped pair is the horizontal mirror.
  const l0 = { x: midX + relLX, y: midY + relLY }
  const r0 = { x: midX + relRX, y: midY + relRY }
  const l1 = { x: midX - relRX, y: midY + relRY }
  const r1 = { x: midX - relLX, y: midY + relLY }
  const flipK = nx < 0 ? -nx : 0
  const targetL = { x: lerp(l0.x, l1.x, flipK), y: lerp(l0.y, l1.y, flipK) }
  const targetR = { x: lerp(r0.x, r1.x, flipK), y: lerp(r0.y, r1.y, flipK) }

  return [
    {
      x: lerp(positions[0]!.x, targetL.x, t),
      y: lerp(positions[0]!.y, targetL.y, t)
    },
    {
      x: lerp(positions[1]!.x, targetR.x, t),
      y: lerp(positions[1]!.y, targetR.y, t)
    }
  ]
}

/**
 * Sphere mix on a sparkle. Almost the full look — REST is already +28° yaw, so
 * damping to ~55% left "look left" at yaw ≈ 0 and the eyes never leave the
 * right pocket (worst on neutre, whose pose *is* REST). Tip-cap / left-higher
 * handle rim rides; mix must still arrive.
 */
export function starEyeMix(
  radii: number[] | null,
  mix: number,
  lookYaw?: number
): number {
  const t = concaveBlendT(radii)
  if (t <= 0) return mix
  let k = 1 - t * 0.05
  // Extra when aiming left of REST — that is the direction damping hides most.
  // Keep look-left as decisive as look-right (REST pocket is already up-right).
  if (lookYaw !== undefined && lookYaw < REST_GAZE.yaw - 8) {
    k = Math.min(1, k + t * 0.16)
  }
  return mix * k
}

/**
 * Map unit-sphere eye coords onto a customiser silhouette.
 *
 * On the sparkle, full radial fit crushed REST sphere eyes off the face orbit
 * (under the top tip) into the waist. Ease only part-way there. Other concave
 * shapes keep a full fit so tips still clear the mask.
 */
export function eyeSurfaceFit(
  _def: Pick<StateDef, 'baseFace' | 'id'>,
  radii: number[] | null,
  px: number,
  py: number,
  rot = 0
): { fitX: number; fitY: number } {
  if (!radii) return { fitX: 1, fitY: 1 }
  const t = concaveBlendT(radii)
  if (t <= 0) return { fitX: 1, fitY: 1 }
  const fit = radiusAtAngle(radii, Math.atan2(py, px) - rot)
  // Sparkle: ease off full outline crush so a later face-orbit lift can sit
  // under the top tip without the waist swallowing the pair.
  const toward = radii === ETOILE_RADII ? t * 0.55 : t
  const full = lerp(1, fit, toward)
  return { fitX: full, fitY: full }
}

/** Resting states reshape eyes on concave profiles; catalogue poses keep video geometry. */
export function eyeStyleForState(
  def: Pick<StateDef, 'baseFace' | 'baseBody' | 'id'>,
  radii: number[] | null,
  cfg: EyeCfg
): ShapeEyeStyle {
  if (!def.baseFace) {
    const pocket = isConcavePocketState(def) ? concavePocketStyle(radii, def.id) : null
    return pocket ?? UNIT_EYE
  }
  return eyeStyleForCfg(radii, cfg)
}

/** Blend from circle eyes toward concave styling as the waist tightens. */
export function eyeStyleForCfg(radii: number[] | null, cfg: EyeCfg): ShapeEyeStyle {
  if (!radii) return UNIT_EYE
  const t = concaveBlendT(radii)
  if (t <= 0) return volumeBodyEyeSeat(radii) ?? UNIT_EYE
  const target = concaveEyeMul(cfg.w, cfg.h, cfg.tilt ?? 0, cfg.open, cfg.form)
  const curve = bottomCurveLift(radii)
  const volume = volumeBodyEyeSeat(radii)
  return {
    w: lerp(1, target.w, t),
    h: lerp(1, target.h, t),
    spread: lerp(1, target.spread, t),
    lift: target.lift * t + curve + (volume?.lift ?? 0),
    inset: lerp(1, target.inset, t),
    tilt: lerp(1, target.tilt, t)
  }
}

/** @deprecated */
export function isSparkleShape(radii: number[] | null): boolean {
  if (!radii) return false
  return profileWaist(radii) < 0.75
}

/** @deprecated */
export function sparkleEyeMul(w: number, h: number, tilt = 0): ShapeEyeStyle {
  return concaveEyeMul(w, h, tilt)
}

/** @deprecated */
export function shapeEyeStyle(radii: number[] | null): ShapeEyeStyle | null {
  if (!radii || profileWaist(radii) >= 0.8) return null
  return concaveEyeMul(0.186, 0.412)
}
